import { useSite } from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  EditMode,
  InstalledInverter,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteElectricalSystem,
  SiteElectricalSystemPlaceholder,
  smartAlphaNumericSort,
  smartAlphaNumericSortInstalledInverters,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { Modal, ModalView } from "@inrange/theme-components";
import { Icons } from "@inrange/theme-components/icons";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  ToggleButton,
  ToggleButtonGroup,
} from "react-bootstrap";
import CollapsibleCard from "../CollapsibleCard";
import { formatNumber, inverterRatedAcOutputPowerMaxRatio } from "./utils";

interface InvertersCardProps {
  site: SiteAllowStringValues;
  esys:
    | AllowStringForNumbers<SiteElectricalSystem>
    | SiteElectricalSystemPlaceholder;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  pvInverterBrand: string;
  isBulkEdit?: boolean;
  editMode: EditMode;
}

const Inverters = ({
  site,
  esys,
  setSite,
  pvInverterBrand,
  isBulkEdit,
  editMode,
}: InvertersCardProps) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });

  const [promptUpdateInverters, setPromptUpdateInverters] =
    useState<boolean>(false);

  const siteRef = useRef<SiteAllowStringValues | undefined>(undefined);
  siteRef.current = site;

  const [rejectedOptimalInverters, setRejectedOptimalInverters] = useState<
    InstalledInverter[]
  >(
    isBulkEdit || editMode === "SUMMARY"
      ? []
      : [
          ...((site.generation[esys.genIdx]
            .invertersOptimal as unknown as InstalledInverter[]) || []),
        ].sort(smartAlphaNumericSortInstalledInverters)
  );

  useEffect(() => {
    if (
      editMode === "SUMMARY" ||
      isBulkEdit ||
      !siteRef.current ||
      siteRef.current.generation[esys.genIdx]?.inverterConfig !== "manual" ||
      // No inverters currently "manually" selected
      siteRef.current.generation[esys.genIdx]?.inverters?.length === 0
    ) {
      return;
    }
    const currentInverters: InstalledInverter[] = [
      ...siteRef.current.generation[esys.genIdx]!.inverters,
    ].sort(smartAlphaNumericSortInstalledInverters);
    const optimalInverters: InstalledInverter[] = [
      ...(siteCalculations.generation?.[esys.genIdx]?.invertersOptimal ?? []),
    ].sort(smartAlphaNumericSortInstalledInverters);
    if (
      // Currently selected inverters are not optimal
      !_.isEqual(currentInverters, optimalInverters) &&
      // And the currently suggested optimal inverters have not been rejected already
      !_.isEqual(rejectedOptimalInverters, optimalInverters)
    ) {
      setPromptUpdateInverters(true);
    }
  }, [
    editMode,
    esys.genIdx,
    isBulkEdit,
    rejectedOptimalInverters,
    siteCalculations.generation,
  ]);

  const isManualInverters =
    site.generation[esys.genIdx]?.inverterConfig === "manual";
  const isAutoInverters =
    site.generation[esys.genIdx]?.inverterConfig === "auto";
  let inverters: InstalledInverter[] =
    editMode === "SUMMARY"
      ? siteCalculations.totalInverters
      : site.generation[esys.genIdx]?.inverterConfig !== "manual"
        ? siteCalculations.generation?.[esys.genIdx]?.inverters || []
        : site.generation[esys.genIdx]?.inverters || [];
  if (isAutoInverters && editMode !== "SUMMARY") {
    inverters = [...inverters].sort(smartAlphaNumericSortInstalledInverters);
  }

  const maxPnomRatio = inverterRatedAcOutputPowerMaxRatio(
    fetchSiteValues.data!.options.pvInverters,
    inverters
  );

  return (
    <CollapsibleCard
      title={`Inverters${inverters.length > 0 ? ` - ${inverters.map((i) => `${i.name.replace(`${pvInverterBrand} `, "")} x ${i.count}`).join(", ")}` : ""}`}
      className="mt-2"
      isBulkEdit={isBulkEdit}
      initialOpen={false}
    >
      <Row>
        <Col>
          <ToggleButtonGroup
            name={`invertersMode-${esys.genIdx}`}
            type="radio"
            value={site.generation[esys.genIdx]?.inverterConfig ?? "none"}
            onChange={(val) =>
              setSite({
                generation: {
                  [esys.genIdx]: {
                    inverterConfig: val,
                    ...(val === "manual" &&
                    site.generation[esys.genIdx]?.inverterConfig === "auto"
                      ? {
                          inverters: [
                            ...(siteCalculations.generation?.[esys.genIdx]
                              ?.inverters || []),
                          ].sort(smartAlphaNumericSortInstalledInverters),
                        }
                      : {}),
                  },
                },
                flags: {
                  cplVersion: "v2",
                },
              })
            }
          >
            <ToggleButton
              id={`tbg-inv-none-${esys.genIdx}`}
              data-testid={`tbg-inv-none-${esys.genIdx}`}
              value={"none"}
            >
              None
            </ToggleButton>
            <ToggleButton
              id={`tbg-inv-manual-${esys.genIdx}`}
              data-testid={`tbg-inv-manual-${esys.genIdx}`}
              value={"manual"}
            >
              Manual
            </ToggleButton>
            <ToggleButton
              id={`tbg-inv-auto-${esys.genIdx}`}
              data-testid={`tbg-inv-auto-${esys.genIdx}`}
              value={"auto"}
            >
              Auto
            </ToggleButton>
          </ToggleButtonGroup>
        </Col>
      </Row>
      {(isAutoInverters || isManualInverters) && (
        <Row className="mb-2 mt-2">
          <Col sm={4}>
            <Form.Group controlId={`inverterTargetAcDcRatioMin-${esys.genIdx}`}>
              <Form.Label>DC:AC Ratio Min</Form.Label>
              <Form.Control
                type="text"
                value={site.generation[esys.genIdx]?.inverterTargetAcDcRatioMin}
                onChange={(e) =>
                  setSite({
                    generation: {
                      [esys.genIdx]: {
                        inverterTargetAcDcRatioMin: e.target.value,
                      },
                    },
                  })
                }
                isInvalid={
                  !!errors[
                    `generation.${esys.genIdx}.inverterTargetAcDcRatioMin`
                  ]
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors[`generation.${esys.genIdx}.inverterTargetAcDcRatioMin`]}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col sm={4}>
            <Form.Group controlId={`inverterTargetAcDcRatioMax-${esys.genIdx}`}>
              <Form.Label>DC:AC Ratio Max</Form.Label>
              <Form.Control
                type="text"
                value={site.generation[esys.genIdx]?.inverterTargetAcDcRatioMax}
                onChange={(e) =>
                  setSite({
                    generation: {
                      [esys.genIdx]: {
                        inverterTargetAcDcRatioMax: e.target.value,
                      },
                    },
                  })
                }
                isInvalid={
                  !!errors[
                    `generation.${esys.genIdx}.inverterTargetAcDcRatioMax`
                  ]
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors[`generation.${esys.genIdx}.inverterTargetAcDcRatioMax`]}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      )}
      {site.generation[esys.genIdx]?.inverterConfig !== undefined &&
        site.generation[esys.genIdx]?.inverterConfig !== "none" && (
          <>
            {!(isBulkEdit && isAutoInverters) && (
              <>
                {inverters.map((inverter, index) => (
                  <Row
                    className="d-flex flex-wrap align-items-end mb-3"
                    key={index}
                  >
                    <Col sm={4}>
                      <Form.Group
                        controlId={`pvInverterBrand-${index}-gen-${esys.genIdx}`}
                      >
                        <Form.Label>Inverter</Form.Label>
                        <Form.Select
                          value={inverter.name}
                          disabled={isAutoInverters}
                          onChange={(e) =>
                            setSite({
                              generation: {
                                [esys.genIdx]: {
                                  inverters: site.generation[
                                    esys.genIdx
                                  ].inverters.map((inv, idx) =>
                                    idx === index
                                      ? { ...inv, name: e.target.value }
                                      : inv
                                  ),
                                },
                              },
                            })
                          }
                        >
                          {Object.keys(
                            fetchSiteValues.data!.options.pvInverters
                          )
                            .sort(smartAlphaNumericSort)
                            .filter(
                              (inverterName) =>
                                fetchSiteValues.data!.options.pvInverters[
                                  inverterName
                                ].manufacturer === pvInverterBrand
                            )
                            .map((inverterName, index) => (
                              <option key={index}>{inverterName}</option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col sm={4}>
                      <Form.Group
                        controlId={`pvInverterCount-${index}-gen-${esys.genIdx}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "auto 32px",
                          gridTemplateRows: "auto auto",
                          gap: "0 8px",
                        }}
                      >
                        <Form.Label
                          style={{
                            gridRow: 1,
                            gridColumn: "span 2",
                          }}
                        >
                          Count
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={inverter.count}
                          disabled={isAutoInverters}
                          style={{
                            gridRow: 2,
                            gridColumn: isAutoInverters ? "span 2" : 1,
                          }}
                          onChange={(e) =>
                            setSite({
                              generation: {
                                [esys.genIdx]: {
                                  inverters: site.generation[
                                    esys.genIdx
                                  ].inverters.map((inv, idx) =>
                                    idx === index
                                      ? {
                                          ...inv,
                                          count: e.target.value,
                                        }
                                      : inv
                                  ),
                                },
                              },
                            })
                          }
                          isInvalid={
                            !!errors[
                              `generation.${esys.genIdx}.inverters.${index}.count`
                            ]
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {
                            errors[
                              `generation.${esys.genIdx}.inverters.${index}.count`
                            ]
                          }
                        </Form.Control.Feedback>

                        {!isAutoInverters && (
                          <div
                            onClick={() =>
                              setSite({
                                generation: {
                                  [esys.genIdx]: {
                                    inverters: site.generation[
                                      esys.genIdx
                                    ].inverters.filter(
                                      (_, idx) => idx !== index
                                    ),
                                  },
                                },
                              })
                            }
                            style={{
                              gridRow: 2,
                              gridColumn: 2,
                              cursor: "pointer",
                              alignSelf: "center",
                            }}
                          >
                            <img
                              alt="remove"
                              src={Icons.trashcanRed}
                              style={{
                                width: "20px",
                                height: "20px",
                                marginLeft: "6px",
                                cursor: "pointer",
                              }}
                            ></img>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    {!isAutoInverters && inverters.length - 1 == index && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "end",
                          width: "auto",
                          padding: "0",
                        }}
                      >
                        <Button
                          variant="success"
                          onClick={() =>
                            setSite({
                              generation: {
                                [esys.genIdx]: {
                                  inverters: [
                                    ...site.generation[esys.genIdx].inverters,
                                    {
                                      name: Object.keys(
                                        fetchSiteValues.data!.options
                                          .pvInverters
                                      ).sort(smartAlphaNumericSort)[0],
                                      count: 1,
                                    },
                                  ],
                                },
                              },
                            })
                          }
                        >
                          +
                        </Button>
                      </div>
                    )}
                  </Row>
                ))}
                {!isAutoInverters &&
                  (site.generation[esys.genIdx]?.inverters?.length ?? 0) ==
                    0 && (
                    <Row className="d-flex flex-wrap align-items-end">
                      <Col sm={4}>
                        <div
                          style={{
                            textAlign: "left",
                            color: "rgb(13, 110, 253)",
                            cursor: "pointer",
                          }}
                        >
                          <a
                            onClick={() =>
                              setSite({
                                generation: {
                                  [esys.genIdx]: {
                                    inverters: [
                                      ...(site.generation[esys.genIdx]
                                        ?.inverters || []),
                                      {
                                        name: Object.keys(
                                          fetchSiteValues.data!.options
                                            .pvInverters
                                        ).sort(smartAlphaNumericSort)[0],
                                        count: 1,
                                      },
                                    ],
                                  },
                                },
                              })
                            }
                          >
                            Add inverter
                          </a>
                        </div>
                      </Col>
                    </Row>
                  )}
              </>
            )}
            <Row className="d-flex flex-wrap align-items-end">
              {!(isBulkEdit && isAutoInverters) &&
                (site.generation[esys.genIdx]?.inverters?.length ?? 0) > 0 && (
                  <>
                    <Col sm={4}>
                      <Form.Group
                        controlId={`inverterPnomRatioMax-${esys.genIdx}`}
                      >
                        <Form.Label>Max spec ratio (DC:AC)</Form.Label>
                        <Form.Control
                          disabled
                          value={formatNumber(maxPnomRatio, 0, 2, false)}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
            </Row>
          </>
        )}
      {!isBulkEdit && promptUpdateInverters && (
        <Modal>
          <ModalView
            id="update-inverters-modal"
            title={<>Update manually selected inverters?</>}
            titlePadding="0"
            fontWeight="500"
            fontSize="18px"
            centerTitle={true}
            width="500px"
          >
            <Table
              striped
              bordered
              style={{ fontSize: "14px", marginTop: "10px" }}
            >
              <thead>
                <tr>
                  <th>Manually selected</th>
                  <th>Suggested updated</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <ul>
                      {site.generation[esys.genIdx]?.inverters.map(
                        (inv, idx) => (
                          <li key={`current-${idx}`}>
                            {inv.count} x {inv.name}
                          </li>
                        )
                      )}
                    </ul>
                  </td>
                  <td>
                    {(siteCalculations.generation?.[esys.genIdx]
                      ?.invertersOptimal?.length ?? 0) > 0 && (
                      <ul>
                        {[
                          ...siteCalculations.generation![esys.genIdx]!
                            .invertersOptimal,
                        ]
                          .sort(smartAlphaNumericSortInstalledInverters)
                          .map((inv, idx) => (
                            <li key={`optimal-${idx}`}>
                              {inv.count} x {inv.name}
                            </li>
                          ))}
                      </ul>
                    )}
                    {(siteCalculations.generation?.[esys.genIdx]
                      ?.invertersOptimal?.length ?? 0) === 0 && (
                      <>
                        No inverters within threshold between{" "}
                        {
                          site.generation[esys.genIdx]
                            ?.inverterTargetAcDcRatioMin
                        }{" "}
                        and {maxPnomRatio}
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
              }}
            >
              <Button
                variant="secondary"
                onClick={() => {
                  setPromptUpdateInverters(false);
                  setRejectedOptimalInverters(
                    [
                      ...(siteCalculations.generation?.[esys.genIdx]
                        ?.invertersOptimal ?? []),
                    ].sort(smartAlphaNumericSortInstalledInverters)
                  );
                }}
              >
                Keep existing
              </Button>
              <Button
                variant="success"
                onClick={() => {
                  setSite({
                    generation: {
                      [esys.genIdx]: {
                        inverters: [
                          ...(siteCalculations.generation?.[esys.genIdx]
                            ?.invertersOptimal || []),
                        ].sort(smartAlphaNumericSortInstalledInverters),
                      },
                    },
                    flags: {
                      cplVersion: "v2",
                    },
                  });
                  setPromptUpdateInverters(false);
                }}
              >
                Update to suggested
              </Button>
            </div>
          </ModalView>
        </Modal>
      )}
    </CollapsibleCard>
  );
};

export default Inverters;
